// Audio kernel module: Buffer
// Manages loading, decoding and plaback of audioBuffers

import * as audio from '../audio.js';

export { load, play, audioBuffers };

// References
const audioBuffers = {};

function load(name, url, progress = null) {
    const request = new XMLHttpRequest();
    const promise = new Promise((resolve, reject) => {
        request.open('GET', url, true);
        request.responseType = 'arraybuffer';
        request.onload = () => {
            resolve(decode(name, request.response));
        };
        request.onerror = () => {
            reject();
            console.error(`An error occured while getting '${name}' audio from '${url}'`);
        };
        request.onprogress = e => {
            const percent = Math.round(e.loaded/e.total * 100);
            if (typeof progress === 'function') progress(percent);
            if (DEVMODE) console.log(`Progress loading '${name}' audio file`, percent + '%');
        };
        request.send();
    });
    return promise;
}

function decode(name, arrayBuffer) {
    const promise = new Promise((resolve, reject) => {
        audio.context.decodeAudioData(arrayBuffer, buffer => {
            audioBuffers[name] = buffer;
            resolve();
            if (DEVMODE) console.log(`Decoded '${name}' audio buffer`);
        },
        e => {
            reject();
            console.error('Error while decoding audio data: ' + e );
        });
    });
    return promise;
}

function play(name, options = {}) {
    if (audioBuffers[name] === undefined) {
        console.error(`Play error: '${name}' audio buffer does not exist.`);
        return false;
    }

    audio.resume();

    // Create a new source
    const source = audio.context.createBufferSource();

    const gainNode = audio.context.createGain();
    if (options.initialGain != undefined) {
        // Note: this initial gain value will only be applied after that time
        // have passed (it does not change immediately).
        gainNode.gain.setValueAtTime(audio.dbToGain(options.initialGain), audio.context.currentTime);
    }
    source.gainNode = gainNode; // custom property for reference
    source.fadeTo = audio.fadeTo; // custom function
    source.fade = audio.fade; // custom function
    source.connect(gainNode);
    gainNode.connect(options.destination || audio.context.destination);

    // Optional looping parameters
    source.loop = options.loop || false;
    source.loopStart = options.loopStart || 0;
    source.loopEnd = options.loopEnd || 0;

    // Optional starting parameters
    const when = (options.when || 0) + audio.context.currentTime;
    const offset = options.offset || 0;
    const duration = options.duration || undefined;

    // ... more options to come (detune, playBackRate)?

    // Assign an already loaded and decoded buffer
    source.buffer = audioBuffers[name];

    // On ended event
    source.onended = () => {
        if (typeof options.onended === 'function') options.onended();
        audio.forget(source);
    }

    // Start producing sound
    if (options.defer === undefined) source.start(when, offset, duration);

    // Maintain references to this source
    audio.sources.push(source);

    return source;
}
